import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Container,
  Paper,
  Link
} from '@mui/material';
import api from '../../utils/api';
import { useLocation } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import useThemeStyles from '../../theme/useThemeStyles';

function SupplierPreRegister() {
  const styles = useThemeStyles();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [success, setSuccess] = useState(false);
  const [referrerName, setReferrerName] = useState(null);
  const [loadingReferral, setLoadingReferral] = useState(false);
  const location = useLocation();
  
  // Extract referral code from URL
  const referralCode = new URLSearchParams(location.search).get('ref');

  // Fetch referral config when referral code is present
  useEffect(() => {
    const fetchReferralConfig = async () => {
      if (!referralCode) {
        setReferrerName(null);
        return;
      }

      try {
        setLoadingReferral(true);
        const response = await api.get(`/api/referrals/config/${referralCode}`);
        if (response.data && response.data.referrerName) {
          setReferrerName(response.data.referrerName);
        }
      } catch (error) {
        // Silently fail - referral code might not exist, that's okay
        setReferrerName(null);
      } finally {
        setLoadingReferral(false);
      }
    };

    fetchReferralConfig();
  }, [referralCode]);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? '' : 'Please enter a valid email address';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    return '';
  };

  const validateName = (name, fieldName) => {
    if (!name.trim()) return `${fieldName} is required`;
    if (name.trim().length < 2) return `${fieldName} must be at least 2 characters long`;
    const nameRegex = /^[A-Za-z\s-']+$/;
    if (!nameRegex.test(name.trim())) {
      return `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`;
    }
    return '';
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    if (!confirmPassword) return 'Please confirm your password';
    if (confirmPassword !== password) return 'Passwords do not match';
    return '';
  };

  // Handle field change
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (generalError) setGeneralError('');
    
    let fieldError = '';
    switch (field) {
      case 'email':
        fieldError = validateEmail(value);
        break;
      case 'password':
        fieldError = validatePassword(value);
        if (formData.confirmPassword) {
          setErrors(prev => ({
            ...prev,
            confirmPassword: validateConfirmPassword(formData.confirmPassword, value)
          }));
        }
        break;
      case 'confirmPassword':
        fieldError = validateConfirmPassword(value, formData.password);
        break;
      case 'firstName':
        fieldError = validateName(value, 'First name');
        break;
      case 'lastName':
        fieldError = validateName(value, 'Last name');
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [field]: fieldError
    }));
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {
      email: validateEmail(formData.email),
      password: validatePassword(formData.password),
      confirmPassword: validateConfirmPassword(formData.confirmPassword, formData.password),
      firstName: validateName(formData.firstName, 'First name'),
      lastName: validateName(formData.lastName, 'Last name')
    };
    
    setErrors(newErrors);
    
    return Object.values(newErrors).every(error => error === '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (loading) return;
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setGeneralError('');
      
      const _response = await api.post('/api/auth/preregister', {
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        role: 'supplier',
        referralCode: referralCode || null
      });
      
      setSuccess(true);
      
    } catch (error) {
      if (error.response?.data?.errors) {
        const serverErrors = {};
        error.response.data.errors.forEach(err => {
          if (err.field) {
            serverErrors[err.field] = err.message;
          } else {
            setGeneralError(err.message || 'Validation failed');
          }
        });
        
        if (Object.keys(serverErrors).length > 0) {
          setErrors(prev => ({ ...prev, ...serverErrors }));
        }
      } else {
        setGeneralError(
          error.response?.data?.message || 
          error.message || 
          'Pre-registration failed. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        backgroundColor: 'secondary.main',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}>
        {/* Logo */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            mb: 4
          }}
        >
          <img src={`${process.env.PUBLIC_URL}/img/idotoosuppliers.svg`} alt="Idotoo Suppliers Logo" style={{ width: '100px', height: '50px' }} />
        </Box>

        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, textAlign: 'center' }}>
                Thank You!
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Your pre-registration was successful. We will notify you when the platform officially opens on Valentine's Day 2026 and you can complete your supplier account setup.
              </Typography>
              <Button
                variant="contained"
                component={RouterLink}
                to="/"
                sx={{ mt: 2 }}
              >
                Return to Home
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'secondary.main',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4
    }}>
      {/* Logo */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mb: 4
        }}
      >
        <img src={`${process.env.PUBLIC_URL}/img/idotoosuppliers.svg`} alt="Idotoo Suppliers Logo" style={{ width: '100px', height: '50px' }} />
      </Box>

      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, textAlign: 'center' }}>
            Pre-Register as a Supplier
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Sign up now and we'll notify you when the platform officially opens on Valentine's Day 2026
          </Typography>

          {referralCode && (loadingReferral || referrerName) && (
            <Alert 
              icon={false}
              sx={{ 
                mb: 2,
                backgroundColor: '#fff3f8',
                border: '1px solid #ffb3d9',
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%',
                  textAlign: 'center'
                }
              }}
            >
              {loadingReferral ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Loading...</Typography>
                </Box>
              ) : referrerName ? (
                <Typography variant="body2" sx={{ textAlign: 'center' }}>
                  💕 The Amazing <strong style={{ color: '#e91e63' }}>{referrerName}</strong> has invited you to become part of the idotoo community! 💕
                </Typography>
              ) : null}
            </Alert>
          )}

          {generalError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {generalError}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="First Name"
            value={formData.firstName}
            onChange={handleChange('firstName')}
            error={!!errors.firstName}
            helperText={errors.firstName}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Last Name"
            value={formData.lastName}
            onChange={handleChange('lastName')}
            error={!!errors.lastName}
            helperText={errors.lastName}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            value={formData.password}
            onChange={handleChange('password')}
            error={!!errors.password}
            helperText={errors.password}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange('confirmPassword')}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            margin="normal"
            required
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Pre-Register'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Want to learn more about idotoo?{' '}
              <Link 
                href="https://www.idotoo.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none' }}
              >
                Visit our website
              </Link>
            </Typography>
          </Box>
        </form>
      </Paper>
    </Container>
    </Box>
  );
}

export default SupplierPreRegister;

